import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  ChartLineUp, Calculator, ForkKnife, Pill, Brain, ChatCircleDots, FileText,
  ShieldCheck, SignOut, Users, ArrowLeft, ClipboardText, Drop, DownloadSimple, HandHeart, MapPinLine, Files, Lock, Heartbeat, Stethoscope,
} from "@phosphor-icons/react";
import { Ribbon } from "lucide-react";
import { Layers } from "lucide-react";
import { usePlatform } from "@/lib/platform";
import { downloadAuthenticatedFile } from "@/lib/download";
import ThemeToggle from "@/components/ThemeToggle";

const PATIENT_NAV = [
  { to: "/", label: "My Care", icon: ChartLineUp, testId: "nav-dashboard", specialty: "any" },
  { to: "/dashboard", label: "Health Metrics", icon: ChartLineUp, testId: "nav-metrics", specialty: "any" },
  { to: "/general-health", label: "General Health", icon: Stethoscope, testId: "nav-general-health", specialty: "any" },
  { to: "/encounters", label: "Check-ins", icon: ChatCircleDots, testId: "nav-encounters", specialty: "any" },
  { to: "/lifepath", label: "Life Path", icon: MapPinLine, testId: "nav-lifepath", specialty: "nephrology" },
  { to: "/labs", label: "Lab Tracker", icon: ShieldCheck, testId: "nav-labs", specialty: "any" },
  { to: "/documents", label: "Documents", icon: Files, testId: "nav-documents", specialty: "any" },
  { to: "/security", label: "Security", icon: Lock, testId: "nav-security", specialty: "any" },
  { to: "/weight", label: "Weight & Fluid", icon: Drop, testId: "nav-weight", specialty: "nephro_or_cardio" },
  { to: "/calculators", label: "Calculators", icon: Calculator, testId: "nav-calculators", specialty: "nephrology" },
  { to: "/diet", label: "Diet Optimizer", icon: ForkKnife, testId: "nav-diet", specialty: "any" },
  { to: "/medications", label: "Medications", icon: Pill, testId: "nav-medications", specialty: "any" },
  { to: "/care-planning", label: "Care Plans", icon: ClipboardText, testId: "nav-care-plans", specialty: "any" },
  { to: "/twin", label: "Digital Twin", icon: Brain, testId: "nav-twin", specialty: "any" },
  { to: "/assistant", label: "AI Assistant", icon: ChatCircleDots, testId: "nav-assistant", specialty: "any" },
  { to: "/summary", label: "Clinical Export", icon: FileText, testId: "nav-summary", specialty: "any" },
];

// Only shown when a provider is scoped-viewing a patient
const PROVIDER_EXTRA = [
  { to: "/care-plan", label: "Care Planning", icon: HandHeart, testId: "nav-care-plan" },
  { to: "/audit", label: "Audit & History", icon: ClipboardText, testId: "nav-audit" },
];

// Cardiology specialty nav — surfaced when the user's specialty is cardiology
// OR when the user is a patient (patients see their own cardiology data when
// their care team has ordered BP/lipid tracking).
const CARDIOLOGY_NAV = [
  { to: "/cardiology", label: "Cardiology", icon: Heartbeat, testId: "nav-cardiology" },
];

// Neurology specialty nav — same visibility rule (patients + neurology providers +
// any provider viewing a patient).
const NEUROLOGY_NAV = [
  { to: "/neurology", label: "Neurology", icon: Brain, testId: "nav-neurology" },
];

// Oncology specialty nav — same visibility rule.
const ONCOLOGY_NAV = [
  { to: "/oncology", label: "Oncology", icon: Ribbon, testId: "nav-oncology" },
];

// Endocrinology specialty nav — same visibility rule (patients + endo providers
// + any provider viewing a patient with endo data).
const ENDOCRINOLOGY_NAV = [
  { to: "/endocrinology", label: "Endocrinology", icon: Drop, testId: "nav-endocrinology" },
];

const PULMONOLOGY_NAV = [
  { to: "/pulmonology", label: "Pulmonology", icon: Heartbeat, testId: "nav-pulmonology" },
];
const GASTRO_NAV = [
  { to: "/gastroenterology", label: "Gastroenterology", icon: ForkKnife, testId: "nav-gastroenterology" },
];
const RHEUM_NAV = [
  { to: "/rheumatology", label: "Rheumatology", icon: HandHeart, testId: "nav-rheumatology" },
];
const ID_NAV = [
  { to: "/infectious-disease", label: "Infectious Disease", icon: ShieldCheck, testId: "nav-infectious-disease" },
];

// Sidebar group definitions — resolves the "20 items overflowing" problem
// by grouping related tools under labelled sections. Each item includes a
// tooltip so hovering explains what the icon does.
const NAV_GROUPS = [
  {
    label: "OVERVIEW",
    items: [
      { to: "/", label: "My Care", icon: Users, testId: "nav-dashboard",
        tooltip: "Consolidated care team — every doctor, every prescription, one screen" },
      { to: "/encounters", label: "Check-ins", icon: ChatCircleDots, testId: "nav-encounters",
        tooltip: "Symptom check-ins & doctor encounters — the clinical timeline" },
      { to: "/general-health", label: "General Health", icon: Stethoscope, testId: "nav-general-health",
        tooltip: "Vitals, USPSTF screenings, immunisations" },
      { to: "/dashboard", label: "Health Metrics", icon: ChartLineUp, testId: "nav-metrics",
        tooltip: "Detailed cross-specialty metrics dashboard (Command Center)" },
    ],
  },
  {
    label: "SPECIALTIES",
    items: [
      { to: "/cardiology", label: "Cardiology", icon: Heartbeat, testId: "nav-cardiology",
        tooltip: "BP, lipids, risk scores, AFib workflow" },
      { to: "/neurology", label: "Neurology", icon: Brain, testId: "nav-neurology",
        tooltip: "NIHSS, GCS, mRS, migraine and seizure diaries" },
      { to: "/oncology", label: "Oncology", icon: Ribbon, testId: "nav-oncology",
        tooltip: "TNM staging, RECIST, chemo, CTCAE toxicities" },
      { to: "/endocrinology", label: "Endocrinology", icon: Drop, testId: "nav-endocrinology",
        tooltip: "HbA1c, thyroid, DKA, hypoglycaemia twin" },
      { to: "/pulmonology", label: "Pulmonology", icon: Heartbeat, testId: "nav-pulmonology",
        tooltip: "Asthma / COPD / PE — ACT, CAT, Wells, GOLD" },
      { to: "/gastroenterology", label: "Gastroenterology", icon: ForkKnife, testId: "nav-gastroenterology",
        tooltip: "Liver (Child-Pugh, MELD), IBD, GI bleed" },
      { to: "/rheumatology", label: "Rheumatology", icon: HandHeart, testId: "nav-rheumatology",
        tooltip: "RA (DAS28, CDAI), SLE, gout — DMARD monitoring" },
      { to: "/infectious-disease", label: "Infectious Disease", icon: ShieldCheck, testId: "nav-infectious-disease",
        tooltip: "Sepsis (qSOFA), CAP (CURB-65), stewardship" },
    ],
  },
  {
    label: "TRACKING",
    items: [
      { to: "/lifepath", label: "Life Path", icon: MapPinLine, testId: "nav-lifepath",
        tooltip: "Longitudinal timeline of every clinical event" },
      { to: "/labs", label: "Lab Tracker", icon: ShieldCheck, testId: "nav-labs",
        tooltip: "eGFR, creatinine, potassium and every past lab" },
      { to: "/weight", label: "Weight & Fluid", icon: Drop, testId: "nav-weight",
        tooltip: "Daily weight, fluid intake, oedema check-ins" },
      { to: "/twin", label: "Digital Twin", icon: Brain, testId: "nav-twin",
        tooltip: "AI simulation of your future disease trajectory" },
    ],
  },
  {
    label: "CLINICAL TOOLS",
    items: [
      { to: "/calculators", label: "Calculators", icon: Calculator, testId: "nav-calculators",
        tooltip: "Every scoring tool — eGFR, KFRE, ASCVD, GRACE, MMSE, HbA1c, etc" },
      { to: "/medications", label: "Medications", icon: Pill, testId: "nav-medications",
        tooltip: "Current regimen, nephrotoxicity checker, prescribe & refill" },
      { to: "/care-planning", label: "Care Plans", icon: ClipboardText, testId: "nav-care-plans",
        tooltip: "Structured multi-week protocols per specialty" },
      { to: "/diet", label: "Diet Optimizer", icon: ForkKnife, testId: "nav-diet",
        tooltip: "Per-specialty nutrition guidance and meal planning" },
      { to: "/assistant", label: "AI Assistant", icon: ChatCircleDots, testId: "nav-assistant",
        tooltip: "Ask Med Nexus AI about your labs and care plan" },
    ],
  },
  {
    label: "RECORDS",
    items: [
      { to: "/documents", label: "Documents", icon: Files, testId: "nav-documents",
        tooltip: "Uploaded records — Claude vision extracts labs, prescriptions, imaging" },
      { to: "/summary", label: "Clinical Export", icon: FileText, testId: "nav-summary",
        tooltip: "FHIR / PDF export of the whole chart" },
      { to: "/standards", label: "Standards · FHIR", icon: FileText, testId: "nav-standards",
        tooltip: "ICD-10 / LOINC / RxNorm coding + FHIR R4 bundle" },
      { to: "/security", label: "Security", icon: Lock, testId: "nav-security",
        tooltip: "MFA, sessions, consent choices, audit trail" },
    ],
  },
  {
    label: "PROVIDER",
    items: [
      { to: "/care-plan", label: "Care Planning", icon: HandHeart, testId: "nav-care-plan",
        tooltip: "Provider-authored care plan for this patient" },
      { to: "/audit", label: "Audit & History", icon: ClipboardText, testId: "nav-audit",
        tooltip: "WORM audit chain — every action ever taken on this chart" },
    ],
  },
];

// Standards & FHIR — always visible; interop is a first-class citizen.
const STANDARDS_NAV = [
  { to: "/standards", label: "Standards · FHIR", icon: Layers, testId: "nav-standards" },
];

export default function Layout({ children }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout, viewingPatient, setViewingPatient } = useAuth();
  const { branding } = usePlatform();
  const platformName = branding?.name || "Med Nexus";
  const isProviderViewing = user?.role === "provider" && viewingPatient;

  // Specialty sidebar tabs are gated on WHERE the current patient has data.
  // Command Center already returns exactly the specialties with any recent
  // clinical signal, so we reuse it as a single source of truth. Refreshes
  // whenever the viewing patient changes.
  const [activeSpecialties, setActiveSpecialties] = useState(() => new Set());
  useEffect(() => {
    // Only fetch inside the patient-shell context (patient login OR provider
    // scoped-viewing). Skips network when unauthenticated / provider on inbox.
    if (!user) { setActiveSpecialties(new Set()); return; }
    if (user.role === "provider" && !viewingPatient) {
      setActiveSpecialties(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/platform/command-center");
        if (cancelled) return;
        const specs = new Set((data?.tiles || []).map((t) => t.specialty));
        setActiveSpecialties(specs);
      } catch {
        if (!cancelled) setActiveSpecialties(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [user, viewingPatient?.patient_id]);

  // A provider must always keep the ability to open the tab from the sidebar
  // when their specialty matches — the tab may be empty but is their landing.
  const showCardiology =
    activeSpecialties.has("cardiology") || user?.specialty === "cardiology";
  const showNeurology =
    activeSpecialties.has("neurology") || user?.specialty === "neurology";
  const showOncology =
    activeSpecialties.has("oncology") || user?.specialty === "oncology";
  const showEndocrinology =
    activeSpecialties.has("endocrinology") || user?.specialty === "endocrinology";
  const showPulmonology =
    activeSpecialties.has("pulmonology") || user?.specialty === "pulmonology" || user?.role === "patient";
  const showGastro =
    activeSpecialties.has("gastroenterology") || user?.specialty === "gastroenterology" || user?.role === "patient";
  const showRheum =
    activeSpecialties.has("rheumatology") || user?.specialty === "rheumatology" || user?.role === "patient";
  const showId =
    activeSpecialties.has("infectious_disease") || user?.specialty === "infectious_disease" || user?.role === "patient";
  // Nephrology-specific patient tools (Life Path, Weight, Calculators, Diet,
  // Digital Twin) are only shown when the patient has nephrology data OR the
  // logged-in provider is a nephrologist. Otherwise this app is a multi-
  // specialty platform, not a CKD-only companion.
  const showNephroTools =
    activeSpecialties.has("nephrology") || user?.specialty === "nephrology";
  // Weight & Fluid is available for BOTH nephrology and cardiology (HF)
  // patients — the two specialties with primary outpatient fluid protocols.
  const showWeightTool =
    showNephroTools ||
    activeSpecialties.has("cardiology") || user?.specialty === "cardiology";
  const filterSpecialtyTag = (nav) => {
    if (nav.specialty === "nephrology") return showNephroTools;
    if (nav.specialty === "nephro_or_cardio") return showWeightTool;
    return true;  // 'any' or unspecified
  };
  const NAV = [
    ...PATIENT_NAV.filter(filterSpecialtyTag),
    ...(showCardiology ? CARDIOLOGY_NAV : []),
    ...(showNeurology ? NEUROLOGY_NAV : []),
    ...(showOncology ? ONCOLOGY_NAV : []),
    ...(showEndocrinology ? ENDOCRINOLOGY_NAV : []),
    ...(showPulmonology ? PULMONOLOGY_NAV : []),
    ...(showGastro ? GASTRO_NAV : []),
    ...(showRheum ? RHEUM_NAV : []),
    ...(showId ? ID_NAV : []),
    ...STANDARDS_NAV,
    ...(isProviderViewing ? PROVIDER_EXTRA : []),
  ];
  const current = NAV.find((n) => n.to === loc.pathname) || NAV[0];

  const backToInbox = () => {
    setViewingPatient(null);
    navigate("/provider");
  };

  return (
    <div className="min-h-screen flex bg-[var(--ros-bg)] text-white">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-[240px] shrink-0 border-r border-[#272E2B] gap-6 sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="flex items-center gap-3 px-6 pt-6">
          <div className="relative w-10 h-10 rounded-2xl bg-[#4ADE80] flex items-center justify-center ros-glow-primary">
            <span className="text-black font-heading font-bold text-lg">{platformName.charAt(0)}</span>
          </div>
          <div>
            <div className="font-heading text-lg tracking-tight">{platformName}</div>
            <div className="ros-label" style={{ fontSize: 9 }}>Multi-Specialty Console</div>
          </div>
        </div>

        {user?.role === "provider" && (
          <button
            onClick={backToInbox}
            data-testid="back-to-inbox"
            className="mx-6 flex items-center gap-2 px-3 py-2 rounded-xl text-sm bg-[#1C201E] hover:bg-[#272E2B] transition-colors"
          >
            <ArrowLeft size={16} />
            <Users size={14} />
            Patient inbox
          </button>
        )}

        {/* Scrollable middle — NAV grouped by section */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 space-y-4 scrollbar-thin"
             data-testid="primary-nav">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((n) => NAV.some((x) => x.to === n.to));
            if (items.length === 0) return null;
            return (
              <div key={group.label}>
                <div className="ros-label px-3 mb-1.5" style={{ fontSize: 9 }}>
                  {group.label}
                </div>
                <div className="flex flex-col gap-0.5">
                  {items.map((n) => {
                    const Icon = n.icon;
                    return (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.to === "/"}
                        data-testid={n.testId}
                        title={n.tooltip || n.label}
                        className={({ isActive }) =>
                          `group flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
                            isActive
                              ? "bg-[#1C201E] text-white"
                              : "text-[#A3ACA7] hover:bg-[#121514] hover:text-white"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon size={18} weight={isActive ? "duotone" : "regular"} />
                            <span className="font-medium text-sm">{n.label}</span>
                            {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#4ADE80]" />}
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="px-6 pb-6 space-y-3 border-t border-[#272E2B] pt-4">
          <div className="ros-card-raised">
            <div className="ros-label" style={{ fontSize: 9 }}>
              {user?.role === "provider" && viewingPatient ? "Viewing patient" : "Signed in as"}
            </div>
            <div className="mt-2 font-heading text-white text-base leading-tight">
              {user?.role === "provider" && viewingPatient ? viewingPatient.name : user?.name}
            </div>
            <div className="text-[10px] text-[#A3ACA7] mt-1">
              {user?.role === "provider" && viewingPatient
                ? `via ${user.name}`
                : `${user?.role} · ${user?.email}`}
            </div>
          </div>
          <button
            onClick={async () => { await logout(); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#A3ACA7] hover:bg-[#1C201E] hover:text-white transition-colors"
          >
            <SignOut size={16} /> Log out
          </button>
          <div className="pt-2">
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {isProviderViewing && (
          <div className="bg-[#4ADE80]/10 border-b border-[#4ADE80]/30"
               data-testid="provider-viewing-banner">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#4ADE80] ros-glow-primary" />
              <span className="text-white/90">
                Provider view · <span className="font-semibold">{viewingPatient.name}</span> ({viewingPatient.email})
              </span>
              {viewingPatient.overall_risk && (
                <span
                  className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{
                    color: viewingPatient.overall_risk === "high" ? "#F87171"
                          : viewingPatient.overall_risk === "medium" ? "#FBBF24" : "#4ADE80",
                    background: viewingPatient.overall_risk === "high" ? "rgba(248,113,113,0.12)"
                              : viewingPatient.overall_risk === "medium" ? "rgba(251,191,36,0.12)"
                              : "rgba(74,222,128,0.12)",
                  }}
                >
                  {viewingPatient.overall_risk.toUpperCase()} RISK
                </span>
              )}
            </div>
            <button onClick={backToInbox} data-testid="return-to-inbox"
                    className="text-[#4ADE80] hover:underline text-xs font-semibold">
              ← Return to inbox
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const label = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                downloadAuthenticatedFile("/provider/rpm/report.pdf", `rpm-${label}.pdf`);
              }}
              data-testid="rpm-pdf-download-btn"
              className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-[#A3ACA7] hover:text-white border border-[#272E2B] rounded-full px-3 py-1 hover:bg-[#272E2B] transition-colors ml-3"
            >
              <DownloadSimple size={12} /> RPM PDF
            </button>
          </div>
          </div>
        )}

        <div className="border-b border-[#272E2B]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="ros-label">{platformName}</div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight" data-testid="page-title">
              {current.label}
            </h1>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-sm text-[#A3ACA7]">
            <span className="w-2 h-2 rounded-full bg-[#4ADE80] ros-glow-primary" />
            System synced
          </div>
          </div>
        </div>
        <motion.div
          key={loc.pathname + (viewingPatient?.patient_id || "")}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="max-w-7xl mx-auto p-6 md:p-10"
        >
          {children}
        </motion.div>

        {/* Mobile nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 border-t border-[#272E2B] bg-[#080A09]/95 backdrop-blur px-2 py-2 flex justify-around">
          {NAV.slice(0, 5).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`${n.testId}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] ${
                    isActive ? "text-[#4ADE80]" : "text-[#A3ACA7]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={20} weight={isActive ? "duotone" : "regular"} />
                    <span>{n.label.split(" ")[0]}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </main>
    </div>
  );
}
